/**
 * Skylite Language Configuration
 * Copyright © 2024 Enterceptor Corp
 * All Rights Reserved
 */

/**
 * Map Skylite app codes (SUPPORTED_LANGUAGES keys) to Google Translate widget language codes.
 * Used with public/auto-translation.js so new pages get machine translation without wiring every string through t().
 */
export const APP_LANGUAGE_TO_GOOGLE = {
  en: 'en',
  ar: 'ar',
  zh: 'zh-CN',
  hi: 'hi',
  es: 'es',
  pt: 'pt',
  id: 'id',
  fr: 'fr'
};

/** Unique list for createAutoTranslationManager({ includedLanguages }) */
export const GOOGLE_TRANSLATE_INCLUDED_LANGUAGES = Array.from(new Set(Object.values(APP_LANGUAGE_TO_GOOGLE)));

export function appLanguageToGoogleCode(appCode) {
  return APP_LANGUAGE_TO_GOOGLE[appCode] || 'en';
}

export const SUPPORTED_LANGUAGES = {
  en: {
    code: 'en',
    name: 'English',
    nativeName: 'English',
    flag: '🇺🇸'
  },
  ar: {
    code: 'ar',
    name: 'Arabic',
    nativeName: 'العربية',
    flag: '🇸🇦'
  },
  zh: {
    code: 'zh',
    name: 'Mandarin',
    nativeName: '中文',
    flag: '🇨🇳'
  },
  hi: {
    code: 'hi',
    name: 'Hindi',
    nativeName: 'हिन्दी',
    flag: '🇮🇳'
  },
  es: {
    code: 'es',
    name: 'Spanish',
    nativeName: 'Español',
    flag: '🇪🇸'
  },
  pt: {
    code: 'pt',
    name: 'Portuguese',
    nativeName: 'Português',
    flag: '🇧🇷'
  },
  id: {
    code: 'id',
    name: 'Indonesian',
    nativeName: 'Bahasa Indonesia',
    flag: '🇮🇩'
  },
  fr: {
    code: 'fr',
    name: 'French',
    nativeName: 'Français',
    flag: '🇫🇷'
  }
};

export const TRANSLATIONS = {
  en: {
    // App UI
    translate: 'Translate',
    terminal: 'Terminal',
    riskAssessment: 'Risk Assessment',
    files: 'Files',
    browser: 'Searchable',
    licenseConsole: 'Licenses',
    stepIntoTheLite: 'Step Into The Lite',
    loading: 'Loading...',
    accessDenied: 'Access Denied',
    tryAgain: 'Try Again',
    youMustBeStudent: 'You must be a student in the LMS to access this application.',
    authenticationFailed: 'Authentication failed. Please try again.',
    // Terminal messages
    disconnected: 'Disconnected. Waiting for server connection...',
    toBegin: 'To begin, ensure the Skylite server is running.',
    onceConnected: 'Once connected, you will see a green status indicator.',
    yourFirstScan: 'Your First Scan',
    toScanTarget: 'To scan a target, type:',
    example: 'Example:',
    connectedToSkylite: 'Connected to Skylite Penetration Testing Terminal',
    welcomeToSkylite: 'Welcome to the Skylite custom penetration testing terminal.',
    typeHelp: 'Type "help" for a list of available commands.',
    terminalNotConnected: 'Terminal not connected',
    maximumConnectionAttempts: 'Maximum connection attempts reached. Please check if the server is running.',
    manuallyReconnect: 'You can manually reconnect by refreshing the page or restarting the application.',
    connectionError: 'Connection error. Retrying...',
    failedToCreateConnection: 'Failed to create WebSocket connection',
    // Terminal commands
    help: 'help',
    clear: 'clear',
    refresh: 'refresh',
    // Network scanning
    networkScanner: 'Network Scanner',
    webEnumeration: 'Web Enumeration',
    exploitFramework: 'Exploit Framework',
    fileOperations: 'File Operations',
    currentTarget: 'Current Target',
    availableCommands: 'Available Commands',
    // Window controls
    minimize: 'Minimize',
    maximize: 'Maximize',
    close: 'Close',
    // Browser
    navigate: 'Navigate',
    home: 'Home',
    back: 'Back',
    forward: 'Forward',
    security: 'Security',
    vulnerabilities: 'Vulnerabilities',
    serverInfo: 'Server Info',
    status: 'Status',
    contentType: 'Content Type',
    poweredBy: 'Powered By',
    securityAnalysis: 'Security Analysis',
    critical: 'Critical',
    warning: 'Warning',
    info: 'Info',
    error: 'Error',
    connectToHackableBox: 'Connect to Hackable Box',
    enterTargetIP: 'Enter the IP address of your assigned hackable box:',
    targetIPAddress: 'Target IP Address',
    connect: 'Connect',
    // Files
    newFolder: 'New Folder',
    newFile: 'New File',
    delete: 'Delete',
    move: 'Move',
    save: 'Save',
    saveAs: 'Save As',
    download: 'Download',
    upload: 'Upload',
    folderName: 'Folder Name',
    fileName: 'File Name',
    createFolder: 'Create Folder',
    selectFolder: 'Select Folder',
    moveFile: 'Move File',
    deleteItem: 'Delete Item',
    confirmDelete: 'Are you sure you want to delete this item?',
    itemDeleted: 'Item deleted successfully',
    errorDeleting: 'Error deleting item',
    fileMoved: 'File moved successfully',
    errorMoving: 'Error moving file',
    folderCreated: 'Folder created successfully',
    errorCreating: 'Error creating folder',
    fileSaved: 'File saved successfully',
    errorSaving: 'Error saving file',
    fileDownloaded: 'File downloaded successfully',
    errorDownloading: 'Error downloading file',
    fileUploaded: 'File uploaded successfully',
    errorUploading: 'Error uploading file',
    fileTooLarge: 'File is too large (max 10MB)',
    noFileSelected: 'No file selected',
    loadingFiles: 'Loading files from cloud storage',
    errorLoading: 'Error loading files',

    // Left rail — lab boxes (use keys, not raw English in components)
    labBoxes: 'Lab boxes',
    box01Lab: 'Box 1 (Scout)',
    box02Lab: 'Box 2 (Web Entry)',
    box03Lab: 'Box 3 (Credential Pivot)',
    box04Lab: 'Box 4 (PrivEsc & Proof)',

    boxLaunchPreparing: 'Preparing...',
    boxLaunchStarting: 'Starting...',
    boxLaunchReady: 'Ready',
    boxLaunchStartFailed: 'Start failed',
    box01ScoutStartFailed: 'Box 01 failed to start Scout target:',
    box01ManifestFlagPanelTitle: 'Box 1 (Scout) — recon milestone',
    box01ManifestFlagPanelBody:
      'You reached the backup manifest. Copy the last line from the page below (or just the SKYLITE{…} token), paste it here, and submit to record completion—then you will see what this box was meant to teach.',
    box01ManifestFlagLoading: 'Loading manifest from the lab…',
    box01ManifestFlagLoadError:
      'Could not load manifest text (check that Scout is running on port 8081 and Skylite’s Inspect/Recon API is available).',
    box01ManifestFlagMissing:
      'No SUBMISSION_FLAG line was found. Click Start on Box 01 in this Skylite session so Scout gets a fresh flag, wait until it is ready, then refresh or open this page again.',
    box01ManifestFlagSubmitHint:
      'Paste the full SUBMISSION_FLAG line or only SKYLITE{…}. Leading commas and spaces inside the braces are cleaned up when you leave the field or submit.',
    box01ManifestFlagPlaceholder: 'Paste from manifest (line or SKYLITE{…})',
    box01ManifestFlagSubmit: 'Submit flag',
    box01ManifestFlagSuccess: 'Flag accepted.',
    box01ManifestFlagErrorGeneric:
      'That flag was not accepted. Copy the SKYLITE{…} value from the manifest below, or start Box 01 again in this browser if the run expired.',
    box01ManifestFlagInvalid:
      'Could not read a valid flag. Paste the SUBMISSION_FLAG line or the SKYLITE{32 hex characters} token from the manifest below.',
    box01ManifestFlagInputLockedAfterSuccess:
      'Flag already accepted — this field is read-only. Refresh the page if you need to submit again after a new Box 01 start.',
    box01ManifestFlagNetworkError: 'Could not reach the server. Check that Skylite is running.',
    box01ManifestFlagBadResponse: 'Unexpected response from the server. Try again.',
    box01ManifestLearnedTitle: 'What you practiced (Box 1 — Scout)',
    box01ManifestLearned1: 'Confirmed the target with /health and read structured hints from /api/status.',
    box01ManifestLearned2: 'Treated robots.txt Disallow paths as discovery hints, not as guarantees you are blocked.',
    box01ManifestLearned3: 'When /backup did not list files, probed a plausible inventory filename (manifest.txt).',
    box01ManifestLearned4: 'Interpreted manifest inventory as information disclosure that guides follow-on testing.',
    box01ManifestLearned5: 'Verified lab completion with the server using the same browser session as Start Box 01.',
    expandLabBoxesPanel: 'Expand lab boxes panel',
    collapseLabBoxesPanel: 'Collapse lab boxes panel',

    // Landing (public sign-in) — t() for first paint; subtree is notranslate so Google does not flash-translate
    landingBrandTitle: 'CommandLine Academy',
    landingWorkforceTraining: 'IT · Software Development · Database Management · Cybersecurity',
    landingHeadline: 'Career Training Platform',
    landingBody:
      'CommandLine Academy is a computer science career training platform. Students build IT, software, database, and cybersecurity skills to start careers in tech.',
    landingAsideBlurb:
      "Sign in with Google to continue. You'll be prompted for your license code if needed, then sent to the training dashboard.",
    landingGoogleCta: 'Continue with Google',
    landingContactQuestions: 'Questions: contact us at',
    landingHintDb:
      'Google sign-in needs SQL Server connected. Fix DB settings in repo-root .env, restart the API, then confirm /api/health returns 200 before using Gmail login.',
    landingHintOAuth: 'Sign-in unavailable: configure GOOGLE_CLIENT_ID / GOOGLE_CLIENT_SECRET on the API.',

    // Owner admin hub (post-login cards — t() + notranslate; same rules as landing)
    ownerConsoleOverline: 'CommandLine · owner',
    ownerHubTitle: 'Admin dashboard',
    ownerHubIntro:
      'Open Basic Training for the core curriculum, Advanced Training (Skylite OS) for labs and cyber range, or issue licenses. Use Home in the top bar anytime to return here.',
    ownerCardSkyliteTitle: 'Advanced Training',
    ownerCardSkyliteBody: 'Skylite OS — terminal, browser, reports, lab boxes, and the full advanced workspace.',
    ownerCardSkyliteCta: 'Open Skylite OS',
    ownerCardTrainingTitle: 'Basic Training',
    ownerCardTrainingBody: 'Course dashboard, levels, and guided labs in the browser.',
    ownerCardTrainingCta: 'Open Basic Training',
    ownerCardLicenseTitle: 'Licenses',
    ownerCardLicenseBody: 'Generate batches, export CSV, and manage institution role license codes.',
    ownerCardLicenseCta: 'Open license console',
    ownerSupportOverline: 'Support',
    ownerSupportTitle: 'Mimic instructor / principal hub',
    ownerSupportBody:
      'Search live license batches by school or instructor email. Opens the institution hub in preview; your owner session stays signed in.',
    ownerSupportDummyUserLabel: 'School or instructor',
    ownerSupportChoose: 'Choose…',
    ownerSupportOpenPreview: 'Open institution hub',
    ownerLogOut: 'Log out',
    ownerDefaultDisplayName: 'Owner',
    ownerStripOpenHub: 'Admin Home',
    ownerLicenseConsoleBack: 'My Dashboard',
    ownerLicenseConsoleTitle: 'License console',
    ownerTaskbarAdminDashboard: 'Admin Home',
    ownerTaskbarReturnHubTitle: 'Return to admin home',
    institutionHubOverline: 'CommandLine · institution hub',
    institutionCardAdvancedTitle: 'Advanced Training',
    institutionCardAdvancedBody: 'Skylite OS — labs, terminal, browser, and reporting for authorized staff.',
    institutionCardAdvancedCta: 'Open Skylite OS',
    institutionCardBasicTitle: 'Basic Training',
    institutionCardBasicBody: 'Course dashboard and progress (opens in browser when configured).',
    institutionCardBasicCta: 'Open Basic Training',
    institutionLicenseExpirationLabel: 'License(s) Expiration Date',
    institutionLicenseExpirationPending: 'Not available yet',
    institutionLicenseExpireSoon: 'Your License Will Expire Soon.',
    institutionLicenseRenewCta: 'Renew',
    trainingNavBackToBasic: 'Back to Cyber Training',
    trainingNavSkyliteOs: 'Skylite OS',
    trainingNavDashboard: 'My Dashboard'
  },
  
  ar: {
    // App UI
    translate: 'ترجم',
    terminal: 'الطرفية',
    riskAssessment: 'تقييم المخاطر',
    files: 'الملفات',
    browser: 'المتصفح',
    stepIntoTheLite: 'ادخل إلى النور',
    loading: 'جاري التحميل...',
    accessDenied: 'تم رفض الوصول',
    tryAgain: 'حاول مرة أخرى',
    youMustBeStudent: 'يجب أن تكون طالباً في نظام إدارة التعلم للوصول إلى هذا التطبيق.',
    authenticationFailed: 'فشل في المصادقة. يرجى المحاولة مرة أخرى.',
    
    // Terminal messages
    disconnected: 'غير متصل. في انتظار اتصال الخادم...',
    toBegin: 'للبدء، تأكد من تشغيل خادم Skylite.',
    onceConnected: 'بمجرد الاتصال، سترى مؤشر حالة أخضر.',
    yourFirstScan: 'مسحك الأول',
    toScanTarget: 'لمسح هدف، اكتب:',
    example: 'مثال:',
    connectedToSkylite: 'متصل بطرفية اختبار الاختراق Skylite',
    welcomeToSkylite: 'مرحباً بك في طرفية اختبار الاختراق المخصصة Skylite.',
    typeHelp: 'اكتب "help" لقائمة الأوامر المتاحة.',
    terminalNotConnected: 'الطرفية غير متصلة',
    maximumConnectionAttempts: 'تم الوصول إلى الحد الأقصى لمحاولات الاتصال. يرجى التحقق من تشغيل الخادم.',
    manuallyReconnect: 'يمكنك إعادة الاتصال يدوياً بتحديث الصفحة أو إعادة تشغيل التطبيق.',
    connectionError: 'خطأ في الاتصال. إعادة المحاولة...',
    failedToCreateConnection: 'فشل في إنشاء اتصال WebSocket',
    
    // Terminal commands
    help: 'مساعدة',
    clear: 'مسح',
    refresh: 'تحديث',
    
    // Network scanning
    networkScanner: 'ماسح الشبكة',
    webEnumeration: 'تعداد الويب',
    exploitFramework: 'إطار الاستغلال',
    fileOperations: 'عمليات الملفات',
    currentTarget: 'الهدف الحالي',
    availableCommands: 'الأوامر المتاحة',
    
    // Window controls
    minimize: 'تصغير',
    maximize: 'تكبير',
    close: 'إغلاق',
    
    // Browser
    navigate: 'التنقل',
    home: 'الرئيسية',
    back: 'رجوع',
    forward: 'تقدم',
    security: 'الأمان',
    vulnerabilities: 'الثغرات',
    serverInfo: 'معلومات الخادم',
    status: 'الحالة',
    contentType: 'نوع المحتوى',
    poweredBy: 'مدعوم بواسطة',
    securityAnalysis: 'تحليل الأمان',
    critical: 'حرج',
    warning: 'تحذير',
    info: 'معلومات',
    error: 'خطأ',
    connectToHackableBox: 'الاتصال بصندوق قابل للاختراق',
    enterTargetIP: 'أدخل عنوان IP للصندوق القابل للاختراق المخصص لك:',
    targetIPAddress: 'عنوان IP الهدف',
    connect: 'اتصال',
    
    // Files
    newFolder: 'مجلد جديد',
    newFile: 'ملف جديد',
    delete: 'حذف',
    move: 'نقل',
    save: 'حفظ',
    saveAs: 'حفظ باسم',
    download: 'تحميل',
    upload: 'رفع',
    folderName: 'اسم المجلد',
    fileName: 'اسم الملف',
    createFolder: 'إنشاء مجلد',
    selectFolder: 'اختيار مجلد',
    moveFile: 'نقل ملف',
    deleteItem: 'حذف عنصر',
    confirmDelete: 'هل أنت متأكد من حذف هذا العنصر؟',
    itemDeleted: 'تم حذف العنصر بنجاح',
    errorDeleting: 'خطأ في حذف العنصر',
    fileMoved: 'تم نقل الملف بنجاح',
    errorMoving: 'خطأ في نقل الملف',
    folderCreated: 'تم إنشاء المجلد بنجاح',
    errorCreating: 'خطأ في إنشاء المجلد',
    fileSaved: 'تم حفظ الملف بنجاح',
    errorSaving: 'خطأ في حفظ الملف',
    fileDownloaded: 'تم تحميل الملف بنجاح',
    errorDownloading: 'خطأ في تحميل الملف',
    fileUploaded: 'تم رفع الملف بنجاح',
    errorUploading: 'خطأ في رفع الملف',
    fileTooLarge: 'الملف كبير جداً (الحد الأقصى 10 ميجابايت)',
    noFileSelected: 'لم يتم اختيار ملف',
    loadingFiles: 'جاري تحميل الملفات من التخزين السحابي',
    errorLoading: 'خطأ في تحميل الملفات',

    labBoxes: 'صناديق المختبر',
    box01Lab: 'الصندوق 1 (الكشاف)',
    box02Lab: 'الصندوق 2 (دخول الويب)',
    box03Lab: 'الصندوق 3 (تحريك الاعتماد)',
    box04Lab: 'الصندوق 4 (تصعيد الامتياز والإثبات)',

    boxLaunchPreparing: 'جاري التحضير...',
    boxLaunchStarting: 'جاري البدء...',
    boxLaunchReady: 'جاهز',
    boxLaunchStartFailed: 'فشل البدء',
    box01ScoutStartFailed: 'فشل بدء الصندوق 1 (الكشاف):',
    expandLabBoxesPanel: 'توسيع لوحة صناديق المختبر',
    collapseLabBoxesPanel: 'طي لوحة صناديق المختبر',

    landingBrandTitle: 'CommandLine Academy',
    landingWorkforceTraining: 'IT · Software Development · Database Management · Cybersecurity',
    landingHeadline: 'منصة التدريب المهني',
    landingBody:
      'CommandLine Academy منصة تدريب مهني في علوم الحاسوب. يبني الطلاب مهارات تقنية المعلومات وتطوير البرمجيات وقواعد البيانات والأمن السيبراني لبدء مسارات مهنية في التقنية.',
    landingAsideBlurb:
      'سجّل الدخول بحساب جوجل للمتابعة. سيُطلب منك رمز الترخيص إذا لزم الأمر، ثم تُوجَّه إلى لوحة التدريب.',
    landingGoogleCta: 'المتابعة باستخدام Google',
    landingContactQuestions: 'أسئلة: تواصل معنا على',
    landingHintDb:
      'يتطلب تسجيل الدخول بجوجل اتصال SQL Server. صحح إعدادات قاعدة البيانات في ملف .env في جذر المستودع، أعد تشغيل واجهة البرمجة، ثم تأكد أن /api/health يعيد 200 قبل استخدام Gmail.',
    landingHintOAuth: 'تسجيل الدخول غير متاح: اضبط GOOGLE_CLIENT_ID و GOOGLE_CLIENT_SECRET على واجهة البرمجة.',

    ownerConsoleOverline: 'وحدة تحكم المالك',
    ownerHubTitle: 'لوحة تحكم الإدارة العامة',
    ownerHubIntro:
      'افتح التدريب لمناهج سطر الأوامر، أو سطح سكايلايت للمختبرات والأدوات، أو إصدار التراخيص. استخدم عنصر لوحة تحكم المسؤول في شريط المهام للعودة إلى هنا في أي وقت.',
    ownerCardSkyliteTitle: 'سطح سكايلايت',
    ownerCardSkyliteBody: 'الطرفية والمتصفح والتقارير وصناديق المختبر ومساحة العمل الكاملة للمالك.',
    ownerCardSkyliteCta: 'ادخل سكايلايت',
    ownerCardTrainingTitle: 'التدريب (عبر سطر الأوامر)',
    ownerCardTrainingBody: 'لوحة التعلم والمستويات ومختبرات السيبراني في المتصفح.',
    ownerCardTrainingCta: 'التدريب المفتوح',
    ownerCardLicenseTitle: 'التراخيص',
    ownerCardLicenseBody: 'إنشاء دفعات وتصدير CSV وإدارة رموز تراخيص الأدوار المؤسسية.',
    ownerCardLicenseCta: 'لوحة تحكم الترخيص المفتوح',
    ownerSupportOverline: 'الدعم (بيانات تجريبية)',
    ownerSupportTitle: 'معاينة لوحة تحليلات المؤسسة كمستخدم',
    ownerSupportBody:
      'اختر حساباً نموذجياً لفتح لوحة على نمط المشرف مع مقاييس وهمية. واجهات البرمجة تبقى على جلسة المالك؛ هذا للواجهة فقط حتى يتوفر انتحال الهوية المدعوم بقاعدة البيانات.',
    ownerSupportDummyUserLabel: 'مستخدم تجريبي',
    ownerSupportChoose: 'اختر…',
    ownerSupportOpenPreview: 'فتح لوحة المؤسسة (معاينة)',
    ownerLogOut: 'تسجيل الخروج',
    ownerDefaultDisplayName: 'المالك',
    ownerStripOpenHub: 'الرئيسية',
    ownerLicenseConsoleBack: 'الرئيسية',
    ownerLicenseConsoleTitle: 'وحدة التحكم في الترخيص',
    ownerTaskbarAdminDashboard: 'الرئيسية الإدارية',
    ownerTaskbarReturnHubTitle: 'العودة إلى الرئيسية الإدارية'
  },
  
  zh: {
    // App UI
    translate: '翻译',
    terminal: '终端',
    riskAssessment: '风险评估',
    files: '文件',
    browser: '浏览器',
    stepIntoTheLite: '步入光明',
    loading: '加载中...',
    accessDenied: '访问被拒绝',
    tryAgain: '重试',
    youMustBeStudent: '您必须是LMS中的学生才能访问此应用程序。',
    authenticationFailed: '身份验证失败。请重试。',
    
    // Terminal messages
    disconnected: '已断开连接。等待服务器连接...',
    toBegin: '要开始，请确保Skylite服务器正在运行。',
    onceConnected: '连接后，您将看到绿色状态指示器。',
    yourFirstScan: '您的第一次扫描',
    toScanTarget: '要扫描目标，请输入：',
    example: '示例：',
    connectedToSkylite: '已连接到Skylite渗透测试终端',
    welcomeToSkylite: '欢迎使用Skylite自定义渗透测试终端。',
    typeHelp: '输入"help"查看可用命令列表。',
    terminalNotConnected: '终端未连接',
    maximumConnectionAttempts: '已达到最大连接尝试次数。请检查服务器是否正在运行。',
    manuallyReconnect: '您可以通过刷新页面或重新启动应用程序来手动重新连接。',
    connectionError: '连接错误。重试中...',
    failedToCreateConnection: '创建WebSocket连接失败',
    
    // Terminal commands
    help: '帮助',
    clear: '清除',
    refresh: '刷新',
    
    // Network scanning
    networkScanner: '网络扫描器',
    webEnumeration: 'Web枚举',
    exploitFramework: '漏洞利用框架',
    fileOperations: '文件操作',
    currentTarget: '当前目标',
    availableCommands: '可用命令',
    
    // Window controls
    minimize: '最小化',
    maximize: '最大化',
    close: '关闭',
    
    // Browser
    navigate: '导航',
    home: '主页',
    back: '后退',
    forward: '前进',
    security: '安全',
    vulnerabilities: '漏洞',
    serverInfo: '服务器信息',
    status: '状态',
    contentType: '内容类型',
    poweredBy: '技术支持',
    securityAnalysis: '安全分析',
    critical: '严重',
    warning: '警告',
    info: '信息',
    error: '错误',
    connectToHackableBox: '连接到可破解盒子',
    enterTargetIP: '输入分配给您的可破解盒子的IP地址：',
    targetIPAddress: '目标IP地址',
    connect: '连接',
    
    // Files
    newFolder: '新建文件夹',
    newFile: '新建文件',
    delete: '删除',
    move: '移动',
    save: '保存',
    saveAs: '另存为',
    download: '下载',
    upload: '上传',
    folderName: '文件夹名称',
    fileName: '文件名',
    createFolder: '创建文件夹',
    selectFolder: '选择文件夹',
    moveFile: '移动文件',
    deleteItem: '删除项目',
    confirmDelete: '确定要删除此项目吗？',
    itemDeleted: '项目删除成功',
    errorDeleting: '删除项目时出错',
    fileMoved: '文件移动成功',
    errorMoving: '移动文件时出错',
    folderCreated: '文件夹创建成功',
    errorCreating: '创建文件夹时出错',
    fileSaved: '文件保存成功',
    errorSaving: '保存文件时出错',
    fileDownloaded: '文件下载成功',
    errorDownloading: '下载文件时出错',
    fileUploaded: '文件上传成功',
    errorUploading: '上传文件时出错',
    fileTooLarge: '文件太大（最大10MB）',
    noFileSelected: '未选择文件',
    loadingFiles: '从云存储加载文件',
    errorLoading: '加载文件时出错',

    labBoxes: '实验箱',
    box01Lab: '实验箱 1（侦察）',
    box02Lab: '实验箱 2（Web 入口）',
    box03Lab: '实验箱 3（凭据转移）',
    box04Lab: '实验箱 4（提权与证明）',

    boxLaunchPreparing: '准备中...',
    boxLaunchStarting: '正在启动...',
    boxLaunchReady: '就绪',
    boxLaunchStartFailed: '启动失败',
    box01ScoutStartFailed: '实验箱 1（侦察）启动失败：',
    expandLabBoxesPanel: '展开实验箱面板',
    collapseLabBoxesPanel: '收起实验箱面板'
  },

  hi: {
    translate: 'अनुवाद',
    terminal: 'टर्मिनल',
    riskAssessment: 'जोखिम मूल्यांकन',
    files: 'फ़ाइलें',
    browser: 'खोज योग्य',
    stepIntoTheLite: 'द लाइट में कदम रखें',
    loading: 'लोड हो रहा है...',
    accessDenied: 'पहुँच अस्वीकृत',
    tryAgain: 'पुनः प्रयास करें',
    youMustBeStudent: 'इस ऐप तक पहुँचने के लिए आपको LMS में छात्र होना चाहिए।',
    authenticationFailed: 'प्रमाणीकरण विफल। कृपया पुनः प्रयास करें।',
    labBoxes: 'लैब बॉक्स',
    box01Lab: 'बॉक्स 1 (स्काउट)',
    box02Lab: 'बॉक्स 2 (वेब प्रवेश)',
    box03Lab: 'बॉक्स 3 (क्रेडेंशियल पिवट)',
    box04Lab: 'बॉक्स 4 (प्रिवilege एस्कलेशन और प्रमाण)',

    boxLaunchPreparing: 'तैयार हो रहा है...',
    boxLaunchStarting: 'प्रारंभ हो रहा है...',
    boxLaunchReady: 'तैयार',
    boxLaunchStartFailed: 'प्रारंभ विफल',
    box01ScoutStartFailed: 'बॉक्स 1 (स्काउट) प्रारंभ करने में विफल:',
    expandLabBoxesPanel: 'लैब बॉक्स पैनल खोलें',
    collapseLabBoxesPanel: 'लैब बॉक्स पैनल बंद करें'
  },
  
  es: {
    // App UI
    translate: 'Traducir',
    terminal: 'Terminal',
    riskAssessment: 'Evaluacion de Riesgo',
    files: 'Archivos',
    browser: 'Navegador',
    stepIntoTheLite: 'Da Un Paso Hacia La Luz',
    loading: 'Cargando...',
    accessDenied: 'Acceso Denegado',
    tryAgain: 'Intentar de Nuevo',
    youMustBeStudent: 'Debe ser un estudiante en el LMS para acceder a esta aplicación.',
    authenticationFailed: 'La autenticación falló. Por favor, inténtelo de nuevo.',
    
    // Terminal messages
    disconnected: 'Desconectado. Esperando conexión del servidor...',
    toBegin: 'Para comenzar, asegurese de que el servidor Skylite este ejecutandose.',
    onceConnected: 'Una vez conectado, verá un indicador de estado verde.',
    yourFirstScan: 'Tu Primer Escaneo',
    toScanTarget: 'Para escanear un objetivo, escribe:',
    example: 'Ejemplo:',
    connectedToSkylite: 'Conectado al Terminal de Pruebas de Penetracion Skylite',
    welcomeToSkylite: 'Bienvenido al terminal personalizado de pruebas de penetracion Skylite.',
    typeHelp: 'Escribe "help" para una lista de comandos disponibles.',
    terminalNotConnected: 'Terminal no conectado',
    maximumConnectionAttempts: 'Se alcanzó el máximo de intentos de conexión. Por favor, verifique si el servidor está ejecutándose.',
    manuallyReconnect: 'Puede reconectar manualmente refrescando la página o reiniciando la aplicación.',
    connectionError: 'Error de conexión. Reintentando...',
    failedToCreateConnection: 'Error al crear conexión WebSocket',
    
    // Terminal commands
    help: 'ayuda',
    clear: 'limpiar',
    refresh: 'actualizar',
    
    // Network scanning
    networkScanner: 'Escáner de Red',
    webEnumeration: 'Enumeración Web',
    exploitFramework: 'Marco de Explotación',
    fileOperations: 'Operaciones de Archivos',
    currentTarget: 'Objetivo Actual',
    availableCommands: 'Comandos Disponibles',
    
    // Window controls
    minimize: 'Minimizar',
    maximize: 'Maximizar',
    close: 'Cerrar',
    
    // Browser
    navigate: 'Navegar',
    home: 'Inicio',
    back: 'Atrás',
    forward: 'Adelante',
    security: 'Seguridad',
    vulnerabilities: 'Vulnerabilidades',
    serverInfo: 'Información del Servidor',
    status: 'Estado',
    contentType: 'Tipo de Contenido',
    poweredBy: 'Desarrollado por',
    securityAnalysis: 'Análisis de Seguridad',
    critical: 'Crítico',
    warning: 'Advertencia',
    info: 'Información',
    error: 'Error',
    connectToHackableBox: 'Conectar a Caja Hackeable',
    enterTargetIP: 'Ingrese la dirección IP de su caja hackeable asignada:',
    targetIPAddress: 'Dirección IP del Objetivo',
    connect: 'Conectar',
    
    // Files
    newFolder: 'Nueva Carpeta',
    newFile: 'Nuevo Archivo',
    delete: 'Eliminar',
    move: 'Mover',
    save: 'Guardar',
    saveAs: 'Guardar Como',
    download: 'Descargar',
    upload: 'Subir',
    folderName: 'Nombre de Carpeta',
    fileName: 'Nombre de Archivo',
    createFolder: 'Crear Carpeta',
    selectFolder: 'Seleccionar Carpeta',
    moveFile: 'Mover Archivo',
    deleteItem: 'Eliminar Elemento',
    confirmDelete: '¿Está seguro de que desea eliminar este elemento?',
    itemDeleted: 'Elemento eliminado exitosamente',
    errorDeleting: 'Error al eliminar elemento',
    fileMoved: 'Archivo movido exitosamente',
    errorMoving: 'Error al mover archivo',
    folderCreated: 'Carpeta creada exitosamente',
    errorCreating: 'Error al crear carpeta',
    fileSaved: 'Archivo guardado exitosamente',
    errorSaving: 'Error al guardar archivo',
    fileDownloaded: 'Archivo descargado exitosamente',
    errorDownloading: 'Error al descargar archivo',
    fileUploaded: 'Archivo subido exitosamente',
    errorUploading: 'Error al subir archivo',
    fileTooLarge: 'El archivo es demasiado grande (máx 10MB)',
    noFileSelected: 'Ningún archivo seleccionado',
    loadingFiles: 'Cargando archivos desde almacenamiento en la nube',
    errorLoading: 'Error al cargar archivos',

    labBoxes: 'Cajas de laboratorio',
    box01Lab: 'Caja 1 (Exploración)',
    box02Lab: 'Caja 2 (Entrada web)',
    box03Lab: 'Caja 3 (Pivot de credenciales)',
    box04Lab: 'Caja 4 (PrivEsc y prueba)',

    boxLaunchPreparing: 'Preparando...',
    boxLaunchStarting: 'Iniciando...',
    boxLaunchReady: 'Listo',
    boxLaunchStartFailed: 'Error al iniciar',
    box01ScoutStartFailed: 'Error al iniciar la caja 1 (Exploración):',
    expandLabBoxesPanel: 'Expandir panel de cajas de laboratorio',
    collapseLabBoxesPanel: 'Contraer panel de cajas de laboratorio'
  },
  
  ja: {
    // App UI
    translate: '翻訳',
    terminal: 'ターミナル',
    riskAssessment: 'リスク評価',
    files: 'ファイル',
    browser: 'ブラウザ',
    stepIntoTheLite: '光の中へ',
    loading: '読み込み中...',
    accessDenied: 'アクセス拒否',
    tryAgain: '再試行',
    youMustBeStudent: 'このアプリケーションにアクセスするには、LMSの学生である必要があります。',
    authenticationFailed: '認証に失敗しました。もう一度お試しください。',
    
    // Terminal messages
    disconnected: '切断されました。サーバー接続を待機中...',
    toBegin: '開始するには、Skyliteサーバーが実行されていることを確認してください。',
    onceConnected: '接続すると、緑色のステータスインジケーターが表示されます。',
    yourFirstScan: '最初のスキャン',
    toScanTarget: 'ターゲットをスキャンするには、入力してください：',
    example: '例：',
    connectedToSkylite: 'Skyliteペネトレーションテストターミナルに接続しました',
    welcomeToSkylite: 'Skyliteカスタムペネトレーションテストターミナルへようこそ。',
    typeHelp: '利用可能なコマンドのリストについては「help」と入力してください。',
    terminalNotConnected: 'ターミナルが接続されていません',
    maximumConnectionAttempts: '最大接続試行回数に達しました。サーバーが実行されているかどうかを確認してください。',
    manuallyReconnect: 'ページを更新するか、アプリケーションを再起動して手動で再接続できます。',
    connectionError: '接続エラー。再試行中...',
    failedToCreateConnection: 'WebSocket接続の作成に失敗しました',
    
    // Terminal commands
    help: 'ヘルプ',
    clear: 'クリア',
    refresh: '更新',
    
    // Network scanning
    networkScanner: 'ネットワークスキャナー',
    webEnumeration: 'Web列挙',
    exploitFramework: 'エクスプロイトフレームワーク',
    fileOperations: 'ファイル操作',
    currentTarget: '現在のターゲット',
    availableCommands: '利用可能なコマンド',
    
    // Window controls
    minimize: '最小化',
    maximize: '最大化',
    close: '閉じる',
    
    // Browser
    navigate: 'ナビゲート',
    home: 'ホーム',
    back: '戻る',
    forward: '進む',
    security: 'セキュリティ',
    vulnerabilities: '脆弱性',
    serverInfo: 'サーバー情報',
    status: 'ステータス',
    contentType: 'コンテンツタイプ',
    poweredBy: 'Powered By',
    securityAnalysis: 'セキュリティ分析',
    critical: '重大',
    warning: '警告',
    info: '情報',
    error: 'エラー',
    connectToHackableBox: 'ハッカブルボックスに接続',
    enterTargetIP: '割り当てられたハッカブルボックスのIPアドレスを入力してください：',
    targetIPAddress: 'ターゲットIPアドレス',
    connect: '接続',
    
    // Files
    newFolder: '新しいフォルダ',
    newFile: '新しいファイル',
    delete: '削除',
    move: '移動',
    save: '保存',
    saveAs: '名前を付けて保存',
    download: 'ダウンロード',
    upload: 'アップロード',
    folderName: 'フォルダ名',
    fileName: 'ファイル名',
    createFolder: 'フォルダを作成',
    selectFolder: 'フォルダを選択',
    moveFile: 'ファイルを移動',
    deleteItem: 'アイテムを削除',
    confirmDelete: 'このアイテムを削除してもよろしいですか？',
    itemDeleted: 'アイテムが正常に削除されました',
    errorDeleting: 'アイテムの削除中にエラーが発生しました',
    fileMoved: 'ファイルが正常に移動されました',
    errorMoving: 'ファイルの移動中にエラーが発生しました',
    folderCreated: 'フォルダが正常に作成されました',
    errorCreating: 'フォルダの作成中にエラーが発生しました',
    fileSaved: 'ファイルが正常に保存されました',
    errorSaving: 'ファイルの保存中にエラーが発生しました',
    fileDownloaded: 'ファイルが正常にダウンロードされました',
    errorDownloading: 'ファイルのダウンロード中にエラーが発生しました',
    fileUploaded: 'ファイルが正常にアップロードされました',
    errorUploading: 'ファイルのアップロード中にエラーが発生しました',
    fileTooLarge: 'ファイルが大きすぎます（最大10MB）',
    noFileSelected: 'ファイルが選択されていません',
    loadingFiles: 'クラウドストレージからファイルを読み込み中',
    errorLoading: 'ファイルの読み込み中にエラーが発生しました',

    labBoxes: 'ラボボックス',
    box01Lab: 'ボックス1（スカウト）',
    box02Lab: 'ボックス2（Webエントリ）',
    box03Lab: 'ボックス3（クレデンシャルピボット）',
    box04Lab: 'ボックス4（権限昇格と証明）',

    boxLaunchPreparing: '準備中...',
    boxLaunchStarting: '起動中...',
    boxLaunchReady: '準備完了',
    boxLaunchStartFailed: '起動に失敗',
    box01ScoutStartFailed: 'ボックス1（スカウト）の起動に失敗:',
    expandLabBoxesPanel: 'ラボボックスパネルを展開',
    collapseLabBoxesPanel: 'ラボボックスパネルを折りたたむ'
  },

  pt: {
    translate: 'Traduzir',
    terminal: 'Terminal',
    riskAssessment: 'Avaliação de risco',
    files: 'Arquivos',
    browser: 'Pesquisável',
    stepIntoTheLite: 'Entre na luz',
    loading: 'Carregando...',
    accessDenied: 'Acesso negado',
    tryAgain: 'Tentar novamente',
    youMustBeStudent: 'Você precisa ser estudante no LMS para acessar este aplicativo.',
    authenticationFailed: 'Falha na autenticação. Tente novamente.',
    labBoxes: 'Caixas de laboratório',
    box01Lab: 'Caixa 1 (Batedor)',
    box02Lab: 'Caixa 2 (Entrada web)',
    box03Lab: 'Caixa 3 (Pivô de credenciais)',
    box04Lab: 'Caixa 4 (PrivEsc e prova)',

    boxLaunchPreparing: 'Preparando...',
    boxLaunchStarting: 'Iniciando...',
    boxLaunchReady: 'Pronto',
    boxLaunchStartFailed: 'Falha ao iniciar',
    box01ScoutStartFailed: 'Falha ao iniciar a Caixa 1 (Batedor):',
    expandLabBoxesPanel: 'Expandir painel de caixas de laboratório',
    collapseLabBoxesPanel: 'Recolher painel de caixas de laboratório'
  },

  id: {
    translate: 'Terjemahkan',
    terminal: 'Terminal',
    riskAssessment: 'Penilaian risiko',
    files: 'Berkas',
    browser: 'Dapat dicari',
    stepIntoTheLite: 'Melangkah ke dalam Cahaya',
    loading: 'Memuat...',
    accessDenied: 'Akses ditolak',
    tryAgain: 'Coba lagi',
    youMustBeStudent: 'Anda harus menjadi siswa di LMS untuk mengakses aplikasi ini.',
    authenticationFailed: 'Autentikasi gagal. Silakan coba lagi.',
    labBoxes: 'Kotak lab',
    box01Lab: 'Kotak 1 (Scout)',
    box02Lab: 'Kotak 2 (Entri web)',
    box03Lab: 'Kotak 3 (Pivot kredensial)',
    box04Lab: 'Kotak 4 (PrivEsc & bukti)',

    boxLaunchPreparing: 'Menyiapkan...',
    boxLaunchStarting: 'Memulai...',
    boxLaunchReady: 'Siap',
    boxLaunchStartFailed: 'Gagal memulai',
    box01ScoutStartFailed: 'Gagal memulai Kotak 1 (Scout):',
    expandLabBoxesPanel: 'Buka panel kotak lab',
    collapseLabBoxesPanel: 'Ciutkan panel kotak lab'
  },

  fr: {
    translate: 'Traduire',
    terminal: 'Terminal',
    riskAssessment: 'Évaluation des risques',
    files: 'Fichiers',
    browser: 'Consultable',
    stepIntoTheLite: 'Entrez dans la lumière',
    loading: 'Chargement...',
    accessDenied: 'Accès refusé',
    tryAgain: 'Réessayer',
    youMustBeStudent: 'Vous devez être étudiant dans le LMS pour accéder à cette application.',
    authenticationFailed: 'Échec de l’authentification. Veuillez réessayer.',
    labBoxes: 'Boîtes de labo',
    box01Lab: 'Boîte 1 (Scout)',
    box02Lab: 'Boîte 2 (Entrée web)',
    box03Lab: "Boîte 3 (Pivot d'identifiants)",
    box04Lab: 'Boîte 4 (PrivEsc et preuve)',

    boxLaunchPreparing: 'Préparation...',
    boxLaunchStarting: 'Démarrage...',
    boxLaunchReady: 'Prêt',
    boxLaunchStartFailed: 'Échec du démarrage',
    box01ScoutStartFailed: 'Échec du démarrage de la boîte 1 (Scout) :',
    expandLabBoxesPanel: 'Développer le panneau des boîtes de labo',
    collapseLabBoxesPanel: 'Réduire le panneau des boîtes de labo'
  }
};

// Get translation for a specific key and language
export const getTranslation = (key, language = 'en') => {
  const en = TRANSLATIONS.en;
  const loc = TRANSLATIONS[language];
  if (loc && Object.prototype.hasOwnProperty.call(loc, key) && loc[key] !== undefined && loc[key] !== '') {
    return loc[key];
  }
  if (en && Object.prototype.hasOwnProperty.call(en, key) && en[key] !== undefined) {
    return en[key];
  }
  return key;
};

// Get all translations for a specific key across all languages
export const getAllTranslations = (key) => {
  const result = {};
  Object.keys(SUPPORTED_LANGUAGES).forEach(lang => {
    result[lang] = getTranslation(key, lang);
  });
  return result;
}; 